import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Stack,
  TextInput,
  Button,
  Group,
  Text,
  Divider,
  Box,
  Flex,
} from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import { IconClock } from "@tabler/icons-react";
import swal from "sweetalert";
import moment from "moment";
import { updateEvvRecord } from "../../services/UserTracking";

const combineDateTime = (date, timeStr) => {
  if (!date) return null;
  const d = new Date(date);
  if (timeStr) {
    const [h, m] = timeStr.split(":");
    d.setHours(parseInt(h) || 0, parseInt(m) || 0, 0, 0);
  }
  return d;
};

const extractTime = (dateVal) => {
  if (!dateVal) return "";
  return moment(dateVal).format("HH:mm");
};

const EditEvvRecordDrawer = ({ opened, onClose, record, onSuccess }) => {
  const [saving, setSaving] = useState(false);

  // Clock-In fields
  const [clockInDate, setClockInDate] = useState(null);
  const [clockInTime, setClockInTime] = useState("");
  const [matchedAddress, setMatchedAddress] = useState("");
  const [serviceAddress, setServiceAddress] = useState("");

  // Clock-Out fields
  const [clockOutDate, setClockOutDate] = useState(null);
  const [clockOutTime, setClockOutTime] = useState("");

  const clockInTimeRef = useRef(null);
  const clockOutTimeRef = useRef(null);

  useEffect(() => {
    if (record && opened) {
      if (record.evvClockIn) {
        const ts = record.evvClockIn.timestamp ? new Date(record.evvClockIn.timestamp) : null;
        setClockInDate(ts);
        setClockInTime(extractTime(ts));
        setMatchedAddress(record.evvClockIn.matchedAddress || "");
        setServiceAddress(record.evvClockIn.serviceAddress || "");
      } else {
        setClockInDate(null);
        setClockInTime("");
        setMatchedAddress("");
        setServiceAddress("");
      }
      if (record.evvClockOut) {
        const ts = record.evvClockOut.timestamp ? new Date(record.evvClockOut.timestamp) : null;
        setClockOutDate(ts);
        setClockOutTime(extractTime(ts));
      } else {
        setClockOutDate(null);
        setClockOutTime("");
      }
    }
  }, [record, opened]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { trackingId: record._id };

      if (record.evvClockIn) {
        const combined = combineDateTime(clockInDate, clockInTime);
        payload.evvClockIn = {
          timestamp: combined?.toISOString(),
          matchedAddress: matchedAddress,
          serviceAddress: serviceAddress,
        };
      }

      if (record.evvClockOut) {
        const combined = combineDateTime(clockOutDate, clockOutTime);
        payload.evvClockOut = {
          timestamp: combined?.toISOString(),
          matchedAddress: matchedAddress,
          serviceAddress: serviceAddress,
        };
      }

      await updateEvvRecord(payload);
      swal({ title: "Success", text: "EVV record updated successfully", icon: "success" });
      onSuccess?.();
      onClose();
    } catch (error) {
      swal({
        title: "Error",
        text: error?.response?.data?.message || "Failed to update EVV record",
        icon: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const staffName = record?.staff?.name ||
    `${record?.staff?.firstName || ""} ${record?.staff?.lastName || ""}`.trim() ||
    "N/A";

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} fz={16}>Edit EVV Record</Text>}
      position="right"
      size="lg"
      padding="md"
      overlayProps={{ opacity: 0.55, blur: 3 }}
      zIndex={1000}
    >
      <Stack spacing="md">
        <Text fz={13} color="dimmed">
          Staff: <strong>{staffName}</strong>
        </Text>

        {record?.evvClockIn && (
          <Box>
            <Divider
              label={<Text fw={600} fz={13}>Clock-In</Text>}
              labelPosition="left"
              mb="sm"
            />
            <Flex gap="sm">
              <DatePickerInput
                label="Clock-In Date"
                placeholder="Select date"
                value={clockInDate}
                onChange={setClockInDate}
                clearable
                style={{ flex: 1 }}
              />
              <TimeInput
                label="Clock-In Time"
                ref={clockInTimeRef}
                value={clockInTime}
                onChange={(e) => setClockInTime(e.target.value)}
                rightSection={
                  <IconClock
                    size={16}
                    style={{ cursor: "pointer" }}
                    onClick={() => clockInTimeRef.current?.showPicker()}
                  />
                }
                style={{ flex: 1 }}
              />
            </Flex>
          </Box>
        )}

        {record?.evvClockOut && (
          <Box>
            <Divider
              label={<Text fw={600} fz={13}>Clock-Out</Text>}
              labelPosition="left"
              mb="sm"
            />
            <Flex gap="sm">
              <DatePickerInput
                label="Clock-Out Date"
                placeholder="Select date"
                value={clockOutDate}
                onChange={setClockOutDate}
                clearable
                style={{ flex: 1 }}
              />
              <TimeInput
                label="Clock-Out Time"
                ref={clockOutTimeRef}
                value={clockOutTime}
                onChange={(e) => setClockOutTime(e.target.value)}
                rightSection={
                  <IconClock
                    size={16}
                    style={{ cursor: "pointer" }}
                    onClick={() => clockOutTimeRef.current?.showPicker()}
                  />
                }
                style={{ flex: 1 }}
              />
            </Flex>
          </Box>
        )}

        {(record?.evvClockIn || record?.evvClockOut) && (
          <Box>
            <Divider
              label={<Text fw={600} fz={13}>Address</Text>}
              labelPosition="left"
              mb="sm"
            />
            <Stack spacing="xs">
              <TextInput
                label="Matched Address"
                value={matchedAddress}
                onChange={(e) => setMatchedAddress(e.target.value)}
              />
              <TextInput
                label="Service Address"
                value={serviceAddress}
                onChange={(e) => setServiceAddress(e.target.value)}
              />
            </Stack>
          </Box>
        )}

        {!record?.evvClockIn && !record?.evvClockOut && (
          <Text fz={13} color="dimmed">
            No EVV data to edit for this record.
          </Text>
        )}

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="yellow"
            c="#000"
            onClick={handleSave}
            loading={saving}
            disabled={!record?.evvClockIn && !record?.evvClockOut}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
};

export default EditEvvRecordDrawer;
